import React, { useEffect, useState } from "react";
import { X, Lock, Heart, Users } from "lucide-react";
import ProfileMediaGallery from "../ProfileMediaGallery";
import { requestPrivateGalleryAccess } from "../../api/privateGallery";
import { normalizeList } from "../../utils/normalize";
import { normalizeProfile, getPhotoUrls, getDisplayName } from "../../utils/normalizeProfile";

const BACKEND_URL = window.location.origin;

/**
 * Resolves a URL to be absolute by prepending backend URL if needed
 */
function ensureAbsoluteUrl(url) {
  if (!url || typeof url !== 'string') return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
}

export function ProfileViewModal({ open, onClose, profile, profileOverride, isSelf = false, source = "unknown" }) {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [open]);
  
  // State for private access request
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessRequested, setAccessRequested] = useState(false);

  if (!open) return null;
  
  // CRITICAL: Normalize the profile to prevent "Franken-profile" bugs
  // This ensures consistent field names regardless of backend response shape
  const effectiveProfile = normalizeProfile(profileOverride || profile);
  if (!effectiveProfile) return null;

  const {
    age,
    user_type,
    relationship_style,
    location_city,
    location_region,
    background,
    gender,
    looking_for,
    bio,
    interests,
    // Private media gating fields
    can_view_private_media,
    private_photos_count,
    private_videos_count,
    private_photos,
    private_videos,
    // Intent Signal v1
    intent_signal,
  } = effectiveProfile;

  // Intent Signal display label mapping
  const INTENT_SIGNAL_LABELS = {
    serious: "Serious",
    friends_first: "Friends-first",
    casual: "Casual",
    exploring: "Exploring",
  };

  const photoUrls = getPhotoUrls(effectiveProfile);
  const hasPhotos = photoUrls.length > 0;
  
  // Handle couple names
  const prettyName = getDisplayName(effectiveProfile);
  const isCoupleProfile = gender === 'couple';
  
  const ageLabel = typeof age === "number" ? `${age}` : null;
  const userTypeLabel = user_type || effectiveProfile.type || null;

  const locationLabel =
    [location_city, location_region].filter(Boolean).join(", ") || null;

  const lookingForList = Array.isArray(looking_for)
    ? looking_for
    : typeof looking_for === "string"
    ? [looking_for]
    : [];

  const interestsList = Array.isArray(interests)
    ? interests
    : typeof interests === "string"
    ? interests.split(",").map((s) => s.trim())
    : [];

  // Private media handling
  const privatePhotosList = normalizeList(private_photos);
  const privateVideosList = normalizeList(private_videos);
  const hasPrivateMedia = (private_photos_count > 0 || private_videos_count > 0) || 
                          (privatePhotosList.length > 0 || privateVideosList.length > 0);
  const canViewPrivate = isSelf || can_view_private_media;
  
  // Handle private access request
  const handleRequestPrivateAccess = async () => {
    const ownerUserId = effectiveProfile?.user_id || effectiveProfile?.userId;
    if (!ownerUserId) return;
    
    setRequestingAccess(true);
    try {
      await requestPrivateGalleryAccess(ownerUserId, "I'd love to see your private photos!");
      setAccessRequested(true);
    } catch (err) {
      console.error("Failed to request private access:", err);
    } finally {
      setRequestingAccess(false);
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 backdrop-blur-sm px-4 overflow-y-auto"
      onClick={(e) => {
        // Close when clicking backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="relative w-full max-w-lg max-h-[90vh] rounded-3xl bg-slate-950 border border-slate-800 shadow-2xl overflow-hidden my-4">
        {/* Close button - positioned outside scroll area with high z-index and larger tap target */}
        {/* touch-manipulation prevents "tap becomes scroll" on mobile */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 z-[9999] inline-flex items-center justify-center rounded-full bg-slate-800/90 p-2.5 text-slate-100 hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-rose-500 min-w-[48px] min-h-[48px] pointer-events-auto select-none touch-manipulation"
          aria-label="Close profile"
          data-testid="profile-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Scrollable content container */}
        <div className="overflow-y-auto max-h-[90vh] pt-2">
          {/* Hero section: Media Gallery with photos + video + private gallery */}
          <div className="relative p-4">
            <ProfileMediaGallery
              photos={photoUrls}
              videoUrl={effectiveProfile?.video_intro_url || effectiveProfile?.video_url}
              displayName={prettyName}
              size="md"
              ownerUserId={effectiveProfile?.user_id || effectiveProfile?.userId || effectiveProfile?.user?.id || null}
              privatePhotos={canViewPrivate ? privatePhotosList : null}
              privateVideoUrl={canViewPrivate ? (privateVideosList[0] || null) : null}
              privateMediaCount={hasPrivateMedia ? (private_photos_count || privatePhotosList.length) + (private_videos_count || privateVideosList.length) : 0}
              privateAccessStatus={canViewPrivate ? "granted" : "none"}
              onRequestPrivateAccess={async (ownerUserId, message) => {
                return await requestPrivateGalleryAccess(ownerUserId, message);
              }}
            />
          </div>

        {/* Content */}
        <div className="px-5 pb-5 pt-3 space-y-4 bg-slate-950">
          {/* Name / meta */}
          <div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Couple icon for couple profiles */}
                  {isCoupleProfile && (
                    <Users className="w-5 h-5 text-pink-400" />
                  )}
                  <h2 className="text-xl font-semibold text-white">
                    {prettyName}
                    {ageLabel ? <span className="ml-1 text-slate-300">· {ageLabel}</span> : null}
                  </h2>
                  {/* User badges */}
                  {(effectiveProfile.is_inner_circle || effectiveProfile.entitlements?.inner_circle?.active) && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-yellow-500/30 bg-yellow-500/10 px-2 py-0.5 text-xs font-semibold text-yellow-200">
                      👑 Inner Circle
                    </span>
                  )}
                  {(effectiveProfile.entitlements?.elite_plus?.active) && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-pink-500/30 bg-pink-500/10 px-2 py-0.5 text-xs font-semibold text-pink-200">
                      ✨ Elite+
                    </span>
                  )}
                  {(effectiveProfile.entitlements?.elite?.active && !effectiveProfile.entitlements?.elite_plus?.active) && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 text-xs font-semibold text-purple-200">
                      ⭐ Elite
                    </span>
                  )}
                  {(effectiveProfile.identity_verified || effectiveProfile.is_verified) && (
                    <span className="inline-flex items-center gap-1 rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-xs font-semibold text-blue-200">
                      ✓ Verified
                    </span>
                  )}
                </div>
                {locationLabel && (
                  <p className="text-sm text-slate-400 mt-0.5">{locationLabel}</p>
                )}
              </div>
              {relationship_style && (
                <span className="inline-flex items-center rounded-full bg-rose-500/10 border border-rose-500/40 px-3 py-1 text-xs font-medium uppercase tracking-wide text-rose-200">
                  {relationship_style.replace("_", " ")}
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap gap-2 text-xs text-slate-400">
              {gender && (
                <span className="rounded-full bg-slate-900/80 border border-slate-800 px-2 py-1">
                  {gender === 'couple' ? 'Couple' : gender}
                </span>
              )}
              {background && (
                <span className="rounded-full bg-slate-900/80 border border-slate-800 px-2 py-1">
                  {background}
                </span>
              )}
              {userTypeLabel && (
                <span className="rounded-full bg-slate-900/80 border border-slate-800 px-2 py-1">
                  {userTypeLabel.replace("_", " ")}
                </span>
              )}
              {/* Intent Signal v1 Badge */}
              {intent_signal && INTENT_SIGNAL_LABELS[intent_signal] && (
                <span 
                  className="rounded-full bg-purple-500/20 border border-purple-500/40 px-2 py-1 text-purple-200"
                  data-testid="profile-modal-intent-badge"
                >
                  {INTENT_SIGNAL_LABELS[intent_signal]}
                </span>
              )}
            </div>
          </div>

          {/* Looking for */}
          {lookingForList.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-[0.2em] mb-1">
                Looking for
              </p>
              <div className="flex flex-wrap gap-1.5">
                {lookingForList.map((lf) => (
                  <span
                    key={lf}
                    className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1 text-xs text-slate-100"
                  >
                    {lf.toString().replace("_", " ")}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Family - subtle display */}
          {effectiveProfile.has_children && effectiveProfile.has_children !== "open" && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-[0.2em] mb-1">
                Family
              </p>
              <div className="flex flex-wrap gap-1.5 text-xs text-slate-300">
                {effectiveProfile.has_children === "yes" && (
                  <span className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1">
                    Has children{effectiveProfile.children_count ? ` (${effectiveProfile.children_count})` : ""}
                  </span>
                )}
                {effectiveProfile.has_children === "expecting" && (
                  <span className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1">
                    Expecting
                  </span>
                )}
                {effectiveProfile.has_children === "no" && (
                  <span className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1">
                    No children
                  </span>
                )}
                {effectiveProfile.children_age_ranges?.length > 0 && (
                  <span className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1">
                    Ages: {effectiveProfile.children_age_ranges.map(r => r.replace("_", "-")).join(", ")}
                  </span>
                )}
                {effectiveProfile.custody_type && (
                  <span className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1">
                    {effectiveProfile.custody_type.replace("_", " ")}
                  </span>
                )}
                {effectiveProfile.homeschooling_status === "yes" && (
                  <span className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1">
                    Homeschooling
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Bio */}
          {bio && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-[0.2em] mb-1">
                About
              </p>
              <p className="text-sm text-slate-100 leading-relaxed whitespace-pre-line">
                {bio}
              </p>
            </div>
          )}

          {/* Interests */}
          {interestsList.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-[0.2em] mb-1">
                Interests
              </p>
              <div className="flex flex-wrap gap-1.5">
                {interestsList.map((interest) => (
                  <span
                    key={interest}
                    className="inline-flex items-center rounded-full bg-slate-900/80 border border-slate-800 px-2.5 py-1 text-xs text-slate-100"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Private Media Section - Gated */}
          {hasPrivateMedia && !isSelf && (
            <div className="border-t border-slate-800 pt-4">
              <p className="text-xs font-semibold text-amber-400 uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5">
                <Lock className="w-3 h-3" />
                Private Media
              </p>
              
              {canViewPrivate ? (
                // Unlocked - show private media
                <div className="space-y-3">
                  {/* Private Photos Grid */}
                  {privatePhotosList.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {privatePhotosList.map((photoUrl, idx) => (
                        <div key={idx} className="aspect-square relative">
                          <img
                            src={ensureAbsoluteUrl(photoUrl)}
                            alt={`Private ${idx + 1}`}
                            className="w-full h-full object-cover rounded-lg border border-amber-600/30"
                          />
                          <div className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-amber-900/80 text-amber-200 text-[10px]">
                            <Lock className="w-2.5 h-2.5 inline mr-0.5" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Private Video */}
                  {privateVideosList.length > 0 && (
                    <div className="aspect-video max-w-xs rounded-lg border border-amber-600/30 bg-black overflow-hidden relative">
                      <video
                        src={ensureAbsoluteUrl(privateVideosList[0])}
                        controls
                        playsInline
                        preload="metadata"
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute top-2 left-2 px-2 py-1 rounded bg-amber-900/80 text-amber-200 text-xs">
                        <Lock className="w-3 h-3 inline mr-1" />
                        Private
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Locked - show unlock prompt
                <div className="rounded-xl bg-amber-900/20 border border-amber-600/30 p-4 text-center">
                  <Lock className="w-8 h-8 mx-auto text-amber-400 mb-2" />
                  <p className="text-sm text-amber-200 mb-1">
                    {prettyName} has {private_photos_count || privatePhotosList.length} private photo{(private_photos_count || privatePhotosList.length) !== 1 ? 's' : ''}
                    {(private_videos_count > 0 || privateVideosList.length > 0) && ' and a private video'}
                  </p>
                  <p className="text-xs text-amber-300/70 mb-3">
                    Match with {prettyName} to unlock their private media
                  </p>
                  
                  {accessRequested ? (
                    <p className="text-xs text-emerald-400 flex items-center justify-center gap-1">
                      <Heart className="w-3 h-3" /> Request sent!
                    </p>
                  ) : (
                    <button
                      onClick={handleRequestPrivateAccess}
                      disabled={requestingAccess}
                      className="px-4 py-2 rounded-full bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition disabled:opacity-50"
                    >
                      {requestingAccess ? "Sending..." : "Request Access"}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Self-view hint */}
          {isSelf && (
            <div className="border-t border-slate-800 pt-3 space-y-2">
              <p className="text-[11px] text-slate-500">
                This is how your profile appears to others in Discover, Matches,
                and the Lounge. Updating your photos and bio can dramatically
                increase your match rate.
              </p>
              
              {/* Show private media status for self */}
              {hasPrivateMedia && (
                <div className="rounded-lg bg-amber-900/20 border border-amber-600/30 p-2">
                  <p className="text-[11px] text-amber-300 flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    You have {privatePhotosList.length} private photo{privatePhotosList.length !== 1 ? 's' : ''}
                    {privateVideosList.length > 0 && ` and ${privateVideosList.length} private video`}
                    <span className="text-amber-400/70"> — visible only to your matches</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
